import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  createAnnouncement,
  getAnnouncements,
  publishAnnouncement,
} from "../../api/announcements";
import { uploadAttachments } from "../../api/attachments";
import AttachmentList from "../common/AttachmentList";
import AttachmentUploader from "../common/AttachmentUploader";
import "./HrAnnouncements.css";

const TARGET_OPTIONS = [
  { value: "ALL", label: "كل الموظفين" },
  { value: "DEPARTMENT", label: "إدارة" },
  { value: "BRANCH", label: "فرع" },
  { value: "EMPLOYEES", label: "موظفين محددين" },
];

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function targetLabel(target, announcement) {
  if (target === "ALL") return "كل الموظفين";
  if (target === "DEPARTMENT") {
    return `إدارة: ${announcement?.department?.name || "—"}`;
  }
  if (target === "BRANCH") {
    return `فرع: ${announcement?.branch?.name || "—"}`;
  }
  if (target === "EMPLOYEES") {
    const count = announcement?.recipientCount ?? announcement?.recipients?.length ?? 0;
    return `موظفين محددين (${count})`;
  }
  return target || "—";
}

function statusLabel(status) {
  if (status === "DRAFT") return "مسودة";
  if (status === "PUBLISHED") return "منشور";
  return status || "—";
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

function HrAnnouncements() {
  const { employeesByBranch, branches, departments } = useData();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [publishingId, setPublishingId] = useState("");

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("ALL");
  const [departmentId, setDepartmentId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [publishNow, setPublishNow] = useState(true);
  const [empSearch, setEmpSearch] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [files, setFiles] = useState([]);
  const [attachmentsRefresh, setAttachmentsRefresh] = useState(0);

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter((e) => e.status !== "terminated"),
    [employeesByBranch],
  );

  const activeDepartments = useMemo(
    () =>
      (departments || []).filter(
        (d) => d.isActive !== false && (d.backendId || d.id),
      ),
    [departments],
  );

  const activeBranches = useMemo(
    () => (branches || []).filter((b) => b.isActive !== false),
    [branches],
  );

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return allEmployees;
    return allEmployees.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        e.code?.toLowerCase().includes(q) ||
        e.jobTitle?.toLowerCase().includes(q),
    );
  }, [allEmployees, empSearch]);

  const filteredItems = useMemo(() => {
    if (listFilter === "all") return items;
    return items.filter((item) => item.status === listFilter);
  }, [items, listFilter]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAnnouncements();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل الإعلانات"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  function resetForm() {
    setTitle("");
    setSummary("");
    setBody("");
    setTarget("ALL");
    setDepartmentId("");
    setBranchId("");
    setSelectedIds([]);
    setPublishNow(true);
    setEmpSearch("");
    setFiles([]);
  }

  function toggleEmployee(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selectAllFiltered() {
    const ids = filteredEmployees.map((e) => e.id);
    setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
  }

  function clearSelection() {
    setSelectedIds([]);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!title.trim() || !body.trim()) {
      setError("العنوان ونص الإعلان مطلوبان");
      return;
    }
    if (target === "DEPARTMENT" && !departmentId) {
      setError("اختر الإدارة");
      return;
    }
    if (target === "BRANCH" && !branchId) {
      setError("اختر الفرع");
      return;
    }
    if (target === "EMPLOYEES" && !selectedIds.length) {
      setError("اختر موظفاً واحداً على الأقل");
      return;
    }

    const payload = {
      title: title.trim(),
      body: body.trim(),
      summary: summary.trim() || undefined,
      target,
      publish: publishNow,
    };
    if (target === "DEPARTMENT") payload.departmentId = departmentId;
    if (target === "BRANCH") payload.branchId = branchId;
    if (target === "EMPLOYEES") payload.employeeIds = selectedIds;

    setSubmitting(true);
    try {
      const created = await createAnnouncement(payload);
      if (files.length && created?.id) {
        try {
          await uploadAttachments("announcement", created.id, files);
        } catch (uploadErr) {
          setError(
            getApiErrorMessage(
              uploadErr,
              "تم إنشاء الإعلان لكن تعذر رفع المرفقات",
            ),
          );
          resetForm();
          await loadItems();
          return;
        }
      }
      setSuccess(
        publishNow
          ? "تم نشر الإعلان وإرسال الإشعارات للمستهدفين"
          : "تم حفظ الإعلان كمسودة",
      );
      resetForm();
      await loadItems();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إنشاء الإعلان"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePublish(id) {
    setError("");
    setSuccess("");
    setPublishingId(id);
    try {
      await publishAnnouncement(id);
      setSuccess("تم نشر الإعلان");
      await loadItems();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر نشر الإعلان"));
    } finally {
      setPublishingId("");
    }
  }

  return (
    <div className="hr-announcements" dir="rtl">
      <div className="hr-announcements-header">
        <div>
          <h1 className="hr-announcements-title">إدارة الإعلانات</h1>
          <p className="hr-announcements-subtitle">
            إرسال إعلان لكل الموظفين أو لإدارة أو فرع أو موظفين محددين
          </p>
        </div>
      </div>

      {error ? <div className="hr-announcements-alert error">{error}</div> : null}
      {success ? (
        <div className="hr-announcements-alert success">{success}</div>
      ) : null}

      <form className="hr-announcements-form" onSubmit={handleSubmit}>
        <h2 className="hr-announcements-section-title">إعلان جديد</h2>

        <label className="hr-announcements-field">
          <span>العنوان</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="عنوان الإعلان"
            required
          />
        </label>

        <label className="hr-announcements-field">
          <span>ملخص مختصر (اختياري — يظهر في الإشعار)</span>
          <input
            type="text"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={300}
            placeholder="ملخص قصير"
          />
        </label>

        <label className="hr-announcements-field">
          <span>نص الإعلان</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="تفاصيل الإعلان..."
            required
          />
        </label>

        <div className="hr-announcements-field">
          <span>الاستهداف</span>
          <div className="hr-announcements-target-row">
            {TARGET_OPTIONS.map((opt) => (
              <label key={opt.value} className="hr-announcements-radio">
                <input
                  type="radio"
                  name="target"
                  value={opt.value}
                  checked={target === opt.value}
                  onChange={() => {
                    setTarget(opt.value);
                    setSelectedIds([]);
                  }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {target === "DEPARTMENT" ? (
          <label className="hr-announcements-field">
            <span>الإدارة</span>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              required
            >
              <option value="">اختر الإدارة</option>
              {activeDepartments.map((d) => (
                <option key={d.backendId || d.id} value={d.backendId || d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {target === "BRANCH" ? (
          <label className="hr-announcements-field">
            <span>الفرع</span>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              required
            >
              <option value="">اختر الفرع</option>
              {activeBranches.map((b) => (
                <option key={b.backendId || b.id} value={b.backendId || b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {target === "EMPLOYEES" ? (
          <div className="hr-announcements-employees">
            <div className="hr-announcements-employees-toolbar">
              <input
                type="search"
                className="hr-announcements-search"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                placeholder="بحث بالاسم أو الكود..."
              />
              <div className="hr-announcements-employees-actions">
                <button type="button" onClick={selectAllFiltered}>
                  تحديد الظاهر
                </button>
                <button type="button" onClick={clearSelection}>
                  مسح التحديد
                </button>
                <span className="hr-announcements-selected-count">
                  {selectedIds.length} مختار
                </span>
              </div>
            </div>
            <div className="hr-announcements-employee-list">
              {filteredEmployees.length === 0 ? (
                <div className="hr-announcements-empty-inline">لا يوجد موظفون</div>
              ) : (
                filteredEmployees.map((emp) => (
                  <label key={emp.id} className="hr-announcements-employee-row">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(emp.id)}
                      onChange={() => toggleEmployee(emp.id)}
                    />
                    <span className="hr-announcements-employee-name">
                      {emp.name}
                    </span>
                    <span className="hr-announcements-employee-meta">
                      {emp.code} · {emp.jobTitle}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}

        <AttachmentUploader
          entityType="announcement"
          files={files}
          onFilesChange={setFiles}
          disabled={submitting}
          label="مرفق (اختياري)"
        />
        <p className="hr-announcements-hint">
          يمكن إرفاق صور أو PDF أو Word أو Excel، ويظهر للمستهدفين مع الإعلان.
        </p>

        <label className="hr-announcements-checkbox">
          <input
            type="checkbox"
            checked={publishNow}
            onChange={(e) => setPublishNow(e.target.checked)}
          />
          نشر فوري وإرسال إشعارات للمستهدفين
        </label>

        <button
          type="submit"
          className="hr-announcements-submit"
          disabled={submitting}
        >
          {submitting
            ? "جاري الحفظ..."
            : publishNow
              ? "نشر الإعلان"
              : "حفظ كمسودة"}
        </button>
      </form>

      <div className="hr-announcements-list-section">
        <div className="hr-announcements-list-header">
          <h2 className="hr-announcements-section-title">الإعلانات السابقة</h2>
          <div className="hr-announcements-filters">
            {[
              { value: "all", label: "الكل" },
              { value: "PUBLISHED", label: "منشور" },
              { value: "DRAFT", label: "مسودة" },
            ].map((f) => (
              <button
                key={f.value}
                type="button"
                className={`hr-announcements-filter-btn${
                  listFilter === f.value ? " active" : ""
                }`}
                onClick={() => setListFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="hr-announcements-empty">جاري التحميل...</div>
        ) : filteredItems.length === 0 ? (
          <div className="hr-announcements-empty">لا توجد إعلانات</div>
        ) : (
          <div className="hr-announcements-list">
            {filteredItems.map((item) => (
              <article key={item.id} className="hr-announcements-card">
                <div className="hr-announcements-card-top">
                  <h3>{item.title}</h3>
                  <span
                    className={`hr-announcements-badge ${
                      item.status === "PUBLISHED" ? "published" : "draft"
                    }`}
                  >
                    {statusLabel(item.status)}
                  </span>
                </div>
                {item.summary ? (
                  <p className="hr-announcements-card-summary">{item.summary}</p>
                ) : null}
                <p className="hr-announcements-card-body">{item.body}</p>
                <AttachmentList
                  entityType="announcement"
                  entityId={item.id}
                  hideIfEmpty
                  refreshKey={attachmentsRefresh}
                />
                {item.status === "DRAFT" ? (
                  <AttachmentUploader
                    entityType="announcement"
                    entityId={item.id}
                    disabled={publishingId === item.id}
                    label="إضافة مرفق"
                    onUploaded={() =>
                      setAttachmentsRefresh((n) => n + 1)
                    }
                  />
                ) : null}
                <div className="hr-announcements-card-meta">
                  <span>{targetLabel(item.target, item)}</span>
                  <span>
                    {item.status === "PUBLISHED"
                      ? `نُشر: ${formatDateTime(item.publishedAt)}`
                      : `أُنشئ: ${formatDateTime(item.createdAt)}`}
                  </span>
                </div>
                {item.status === "DRAFT" ? (
                  <button
                    type="button"
                    className="hr-announcements-publish-btn"
                    disabled={publishingId === item.id}
                    onClick={() => handlePublish(item.id)}
                  >
                    {publishingId === item.id ? "جاري النشر..." : "نشر الآن"}
                  </button>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HrAnnouncements;
